import React, { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import {
  AppBar,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Box,
  Grid,
  IconButton,
  TableContainer,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { Search, Print } from "@mui/icons-material";
import logo from "./assets2/logo.png";
import Accountars from "./Accountars";

const Accounts = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [viewAll, setViewAll] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]); // State for fetched data
  const [error, setError] = useState(null); // State for error handling
  const [loading, setLoading] = useState(true); // Add loading state

  const isMobile = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(max-width:1024px)");

  useEffect(() => {
    // Fetch invoice_bills data from the backend
    const fetchInvoiceData = async () => {
      setLoading(true); // Set loading to true before fetch
      try {
        const response = await fetch("https://enzo-matr.onrender.com/api/invoice_bills"); // Ensure the correct API endpoint
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setInvoiceData(data);
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setError("Failed to fetch invoice data. Please try again later.");
      } finally {
        setLoading(false); // Set loading to false after fetch
      }
    };

    fetchInvoiceData();
  }, []);

  // Utility function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return "Invalid Date"; // Handle missing or undefined dates

    const [month, day, year] = dateString.split("/");
    if (!month || !day || !year) return "Invalid Date"; // Handle improperly formatted dates

    const date = new Date(`${year}-${month}-${day}`);
    if (isNaN(date.getTime())) return "Invalid Date"; // Handle invalid date values

    return new Intl.DateTimeFormat("en-US", { month: "long", day: "2-digit", year: "numeric" }).format(date);
  };

  // Filter data based on search term and month
  const filteredData = invoiceData.filter((row) => {
    const matchesSearch = row.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth =
      selectedMonth === "All" ||
      new Date(row.date).toLocaleString("en-US", { month: "long" }) === selectedMonth;
    return viewAll || (matchesSearch && matchesMonth);
  });

  // Calculate unique dates
  const totalUniqueDates = new Set(filteredData.map((row) => row.date)).size;

  const startDate = filteredData.length > 0 ? formatDate(filteredData[0].date) : "N/A";
  const endDate = filteredData.length > 0 ? formatDate(filteredData[filteredData.length - 1].date) : "N/A";

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Table</title>
          <style>
            table {
              width: 80%;
              border-collapse: collapse;
            }
            th, td {
              padding: 4px;
              text-align: center;
            }
            th {
              background-color: #eee !important;
              font-weight: bold;
              border: black;
            }
            td {
              border-top: black;
              border-bottom: black;
            }
            .print-header {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 16px;
            }
            .print-header img {
              width: 24px;
              height: 24px;
              margin-right: 8px;
            }
            .print-header span {
              font-size: 18px;
              font-weight: bold;
            }
            .print-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 16px;
            }
            .print-table {
              width: 100%;
              border-collapse: collapse;
            }
            .print-table th, .print-table td {
              padding: 8px;
              text-align: center;
            }
            .print-table th {
              background-color: #eee !important;
              font-weight: bold;
            }
            .print-table tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .print-table tr:nth-child(odd) {
              background-color: #fff;
            }
            @media print {
              .print-table th {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                background-color: #eee !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <img src="${logo}" alt="logo" />
            <span>Company Name (Income)</span>
          </div>
          <div class="print-info">
            <span>Total Invoice No: 200540 to 200590</span>
            <span>${startDate} to ${endDate}</span>
          </div>
          <table class="print-table">
            <thead>
              <tr>
                ${["Date", "Invoice No.", "Invoice Count", "Tot Qty", "VAT", "Amount"].map((head) => `
                  <th>${head}</th>
                `).join("")}
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((row, i) => `
                <tr>
                  <td>${formatDate(row.date)}</td>
                  <td>${row.invoiceNo}</td>
                  <td>${row.totalItems}</td>
                  <td>${row.products.reduce((sum, product) => sum + (parseFloat(product.quantity) || 0), 0)}</td>
                  <td>${row.products.reduce((sum, product) => sum + (parseFloat(product.vat) || 0), 0)}</td>
                  <td>${parseFloat(row.totalPrice || 0).toFixed(2)}</td>
                </tr>
              `).join("")}
              <tr>
                <td colSpan="7" style="border-bottom: 2px solid black;"></td>
              </tr>
              <tr>
                <td style="text-align: right;">Tot Dates: ${totalUniqueDates}</td>
                <td>Tot Invoice No: ${new Set(filteredData.map(row => row.invoiceNo)).size}</td>
                <td> Tot Invoice : ${filteredData.reduce((sum, row) => sum + row.totalItems, 0)}</td>
                <td> Tot Qty : ${filteredData.reduce((sum, row) => sum + row.products.reduce((qtySum, product) => qtySum + (parseFloat(product.quantity) || 0), 0), 0)}</td>
                <td>Tot VAT : ${filteredData.reduce(
                  (sum, row) =>
                    sum +
                    row.products.reduce((vatSum, product) => vatSum + (parseFloat(product.vat) || 0), 0),
                  0
                )}</td>
                <td> Total Income :${filteredData.reduce((sum, row) => sum + parseFloat(row.totalPrice || 0), 0).toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box sx={{ backgroundColor: "#f4f4f4", minHeight: "220", overflow: "hidden" }}>
      {/* Navbar */}
      <AppBar position="static" sx={{ backgroundColor: "#FFB400", boxShadow: "none" }}>
        <div style={{
          fontFamily: "caladea", width: "99.4%", background: "#fdb913", height: "35px", padding: 4,
          textAlign: "center", fontSize: "30px", color: "white", fontWeight: "bold"
        }}>
          ACCOUNTS
        </div>
      </AppBar>
      <Grid sx={{
        display: "flex", gap: 0.5, flexDirection: isMobile ? "column" : "row",
        padding: isMobile ? "10px" : "0px", justifyContent: "center"
      }}>
        {/* Main Content */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, mt: 1, marginLeft: "20px" }}>
          {/* Error Message */}
          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: "center", marginBottom: 2 }}>
              {error}
            </Typography>
          )}
          {/* Product Income Box */}
          <Paper sx={{ p: 1, width: 680, borderRadius: "8px", boxShadow: 3, display: "flex", fontFamily: "Canada Serif, serif", flexDirection: "column" }}>
            <Typography variant="h6" sx={{ backgroundColor: "#333", color: "#fff", p: 1, fontFamily: "Canada Serif, serif", fontWeight: "bold", textAlign: "center", borderRadius: "4px" }}>
              PRODUCT INCOME
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 1 }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search Invoice No."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flexGrow: 1, backgroundColor: "#fff", borderRadius: "10px", width: "50px" }}
              />
              <IconButton sx={{ backgroundColor: "#000", color: "#fff", fontFamily: "Canada Serif, serif", borderRadius: "6px", width: 36, height: 36 }}>
                <Search />
              </IconButton>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#000", color: "#fff", fontFamily: "Canada Serif, serif", fontWeight: "bold", borderRadius: "10px", border: "2px solid black", height: "36px" }}
                onClick={() => setViewAll(!viewAll)}
              >
                viewAll
              </Button>
              <Select
                size="small"
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  setViewAll(false); // Reset View All when selecting a month
                }}
                sx={{ backgroundColor: "#FFB400", color: "#000", fontFamily: "Canada Serif, serif", fontWeight: "bold", borderRadius: "10px", border: "2px solid black", height: "36px" }}
              >
                <MenuItem value="All">Month</MenuItem>
                {[
                  "January", "February", "March", "April", "May", "June",
                  "July", "August", "September", "October", "November", "December"
                ].map((month) => (
                  <MenuItem key={month} value={month}>{month}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* Income Table */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ textAlign: "center", fontFamily: "Canada Serif, serif", fontWeight: "bold", fontSize: "18px" }}>
                <img src={logo} alt="fire" style={{ width: 44, height: 24, paddingBottom: 10, verticalAlign: "middle" }} />
                <span style={{ fontSize: "20px", marginLeft: -1 }}>Company Name (Income)</span>
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                <Typography variant="body2" sx={{ fontSize: "14px", textAlign: "left", fontFamily: "Canada Serif, serif", fontWeight: "bold" }}>
                  Total Invoice Count: {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px", fontFamily: "Canada Serif, serif", textAlign: "right", fontWeight: "bold" }}>
                  {startDate} to {endDate}
                </Typography>
              </Box>
            </Box>

            {/* Scrollable Table */}
            <TableContainer
              sx={{
                height: "194px",
                overflowY: "auto",
                mt: 2,
                "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar for Chrome, Safari
                "-ms-overflow-style": "none", // Hide scrollbar for Edge
                "scrollbar-width": "none", // Hide scrollbar for Firefox
              }}
            >

              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#eee" }}>
                    {["Date", "Invoice No.", "Invoice Count", "Tot Qty", "VAT", "Amount"].map((head) => (
                      <TableCell key={head} sx={{ fontWeight: "bold", fontFamily: "Canada Serif, serif", textAlign: "center", padding: "6px 8px", fontSize: "12px", borderBottom: "2px solid black", backgroundColor: "#eee" }}>
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{formatDate(row.date)}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.invoiceNo}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>{row.totalItems}</TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>
                          {row.products.reduce((sum, product) => sum + (parseFloat(product.quantity) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>
                          {row.products.reduce((sum, product) => sum + (parseFloat(product.vat) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center", padding: "6px 8px" }}>
                          {parseFloat(row.totalPrice || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", padding: "20px" }}>
                        {loading ? (
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <CircularProgress
                              size={40}
                              thickness={5}
                              sx={{
                                color: "#FFB400",
                                '& .MuiCircularProgress-circle': { stroke: "#000" }, // inner color
                              }}
                            />
                            <Typography sx={{ mt: 1, color: "#FFB400", fontWeight: "bold" }}>
                              Loading...
                            </Typography>
                          </Box>
                        ) : (
                          "No matching records found"
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Separator Line */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ borderBottom: "2px solid black", padding: "0" }} />
                  </TableRow>

                  {/* Totals Row */}
                  <TableRow sx={{ position: "sticky", bottom: 0, backgroundColor: "#eee", zIndex: 2, borderTop: "2px solid black" }}>
                    <TableCell sx={{ textAlign: "right", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      Tot Dates: {totalUniqueDates}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>Tot Invoice No: {new Set(filteredData.map(row => row.invoiceNo)).size}</TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredData.reduce((sum, row) => sum + row.totalItems, 0)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredData.reduce((sum, row) => sum + row.products.reduce((qtySum, product) => qtySum + (parseFloat(product.quantity) || 0), 0), 0)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredData.reduce(
                        (sum, row) =>
                          sum +
                          row.products.reduce((vatSum, product) => vatSum + (parseFloat(product.vat) || 0), 0),
                        0
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", padding: "6px 8px", fontWeight: "bold", fontSize: "13px", fontFamily: "Canada Serif, serif" }}>
                      {filteredData.reduce((sum, row) => sum + parseFloat(row.totalPrice || 0), 0).toFixed(1)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Income Table */}
          <Card sx={{ width: 700, backgroundColor: "#000", color: "#fff", textAlign: "center", fontFamily: "Canada Serif, serif", borderRadius: "8px", boxShadow: 3, height: "99px", mt: -1 }}>
            <CardContent sx={{ padding: "10px" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "13px", textAlign: "left", fontFamily: "Canada Serif, serif" }}>
                  TOTAL INCOME: {filteredData.reduce((sum, row) => sum + parseFloat(row.totalPrice || 0), 0).toFixed(1)}/-
                </Typography>
                <Box sx={{ textAlign: "right" }}>
                  <Typography sx={{ fontSize: "8px" }}>Tot Dates: {totalUniqueDates}</Typography>
                  <Typography sx={{ fontSize: "8px" }}>Tot Invoice Count: {filteredData.reduce((sum, row) => sum + row.totalItems, 0)}</Typography>
                  <Typography sx={{ fontSize: "8px" }}>Tot Income: {filteredData.reduce((sum, row) => sum + parseFloat(row.totalPrice || 0), 0).toFixed(1)}</Typography>
                </Box>
              </Box>
              <Button variant="contained" fullWidth sx={{ mt: 1, backgroundColor: "#FFB400", padding: "6px 225px", color: "#000", fontWeight: "bold", fontFamily: "Canada Serif, serif", borderRadius: "6px", fontSize: "15px" }} startIcon={<Print sx={{ fontSize: "60px" }} />} onClick={handlePrint}>
                PRINT INCOME
              </Button>
            </CardContent>
          </Card>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ width: isMobile ? "100%" : isTablet ? "50%" : "30%" }} >
            <Accountars />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Accounts;